import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi, downloadBlob } from "../../lib/api";
import { eur } from "../../lib/format";
import { Download, Send, CheckCircle, Pencil } from "lucide-react";
import { toast } from "sonner";

const PayStatus = ({ iv, onSend, onPaid, sending }) => {
  if (iv.paid) return <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-sm" data-testid={`paid-${iv.number}`}><CheckCircle className="h-3 w-3" /> Pagada</span>;
  return (
    <div className="flex flex-col items-start gap-1">
      <div className="flex gap-1">
        <button type="button" disabled={sending} onClick={() => onSend(iv)} className="inline-flex items-center gap-1 text-xs bg-amber-500 text-white px-2 py-1 rounded-sm hover:bg-amber-600 disabled:opacity-50" data-testid={`send-pay-link-${iv.number}`}>
          <Send className="h-3 w-3" /> Enviar enlace de pago
        </button>
        <button type="button" disabled={sending} onClick={() => onPaid(iv)} className="inline-flex items-center gap-1 text-xs bg-emerald-600 text-white px-2 py-1 rounded-sm hover:bg-emerald-700 disabled:opacity-50" data-testid={`mark-paid-${iv.number}`}>
          <CheckCircle className="h-3 w-3" /> Marcar pagada
        </button>
      </div>
      {iv.pay_link_sent_at && <span className="text-[10px] text-muted-foreground">Enviado {new Date(iv.pay_link_sent_at).toLocaleString("es-ES")}</span>}
    </div>
  );
};

export default function AdminInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [sending, setSending] = useState("");
  const [filter, setFilter] = useState("all");
  const load = () => adminApi.get("/admin/invoices").then((r) => setInvoices(r.data));
  useEffect(() => { load(); }, []);

  const pending = invoices.filter((i) => !i.paid);
  const pendingTotal = pending.reduce((s, i) => s + (Number(i.total) || 0), 0);
  const shown = filter === "pending" ? pending : filter === "paid" ? invoices.filter((i) => i.paid) : invoices;

  const sendPayLink = async (iv) => {
    setSending(iv.id);
    try {
      const r = await adminApi.post(`/admin/orders/${iv.order_id}/send-pay-link`);
      toast.success(`Enlace de pago enviado a ${r.data.to}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo enviar el enlace");
    } finally {
      setSending("");
    }
  };
  const markPaid = async (iv) => {
    if (!window.confirm(`¿Confirmar que el pedido ${iv.order_number} está pagado por transferencia? Se avisará al cliente por email.`)) return;
    setSending(iv.id);
    try {
      await adminApi.post(`/admin/orders/${iv.order_id}/confirm-payment`);
      toast.success("Pago confirmado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo confirmar el pago");
    } finally {
      setSending("");
    }
  };


  const resendInvoice = async (iv) => {
    if (!window.confirm(`¿Reenviar la factura ${iv.number} en PDF a ${iv.email || "el cliente"}?`)) return;
    setSending(iv.id);
    try {
      const r = await adminApi.post(`/admin/invoices/${iv.id}/resend`);
      toast.success(`Factura reenviada a ${r.data.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo reenviar la factura");
    } finally {
      setSending("");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading font-bold text-3xl tracking-tighter">Facturas</h1>
        <div className="flex gap-2">
          <button onClick={() => downloadBlob("/admin/export/invoices", "facturas.csv")} className="inline-flex items-center gap-2 border border-border rounded-sm px-3 py-2 text-sm hover:border-brand" data-testid="export-csv"><Download className="h-4 w-4" /> CSV</button>
          <button onClick={() => downloadBlob("/admin/export-xlsx/invoices", "facturas.xlsx")} className="inline-flex items-center gap-2 bg-emerald-600 text-white rounded-sm px-3 py-2 text-sm hover:bg-emerald-700" data-testid="export-xlsx"><Download className="h-4 w-4" /> Excel</button>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="inline-flex border border-border rounded-sm overflow-hidden text-sm" data-testid="invoice-filter">
          {[["all", `Todas (${invoices.length})`], ["pending", `Pendientes de pago (${pending.length})`], ["paid", `Pagadas (${invoices.length - pending.length})`]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setFilter(k)} className={`px-3 py-1.5 ${filter === k ? "bg-brand-ink text-white" : "bg-white hover:bg-secondary"}`} data-testid={`invoice-filter-${k}`}>{l}</button>
          ))}
        </div>
        <div className={`rounded-sm px-4 py-2 text-sm ${pendingTotal > 0 ? "bg-amber-50 border border-amber-200 text-amber-900" : "bg-emerald-50 border border-emerald-200 text-emerald-900"}`} data-testid="pending-total">
          Total pendiente de cobro: <strong className="font-heading text-base">{eur(pendingTotal)}</strong>
          <span className="text-xs ml-2 opacity-70">({pending.length} factura{pending.length === 1 ? "" : "s"})</span>
        </div>
      </div>
      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-ink text-white"><tr><th className="text-left px-3 py-2">Nº Factura</th><th className="text-left px-3 py-2">Fecha</th><th className="text-left px-3 py-2">Pedido</th><th className="text-left px-3 py-2">Cliente</th><th className="text-right px-3 py-2">Total</th><th className="text-left px-3 py-2">Pago</th><th className="text-right px-3 py-2">Acciones</th></tr></thead>
          <tbody>
            {shown.length === 0 ? <tr><td colSpan={7} className="py-10 text-center text-muted-foreground">{filter === "pending" ? "No hay facturas pendientes de pago." : "Sin facturas. Genere facturas desde el detalle de cada pedido."}</td></tr> :
             shown.map((iv, i) => (
              <tr key={iv.id} className={i % 2 ? "bg-secondary/40" : "bg-white"} data-testid={`invoice-row-${iv.number}`}>
                <td className="px-3 py-2 font-mono">{iv.number}</td>
                <td className="px-3 py-2 text-muted-foreground">{iv.date}</td>
                <td className="px-3 py-2 font-mono text-xs">{iv.order_number}</td>
                <td className="px-3 py-2">{iv.customer_name}<span className="block text-xs text-muted-foreground">{iv.company}</span></td>
                <td className="px-3 py-2 text-right font-medium">{eur(iv.total)}</td>
                <td className="px-3 py-2"><PayStatus iv={iv} onSend={sendPayLink} onPaid={markPaid} sending={sending === iv.id} /></td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button type="button" onClick={() => resendInvoice(iv)} disabled={sending === iv.id} className="text-emerald-700 hover:underline mr-3 disabled:opacity-50" title="Reenviar al cliente" data-testid={`invoice-resend-${iv.number}`}><Send className="h-4 w-4 inline" /></button>
                  <Link to={`/admin/facturas/${iv.id}/editar`} className="text-foreground hover:text-brand mr-3" title="Editar factura" data-testid={`invoice-edit-${iv.number}`}><Pencil className="h-4 w-4 inline" /></Link>
                  <button type="button" onClick={() => downloadBlob(`/admin/invoices/${iv.id}/pdf`, `${iv.number}.pdf`)} className="text-brand hover:underline" title="Descargar PDF" data-testid={`invoice-pdf-${iv.number}`}><Download className="h-4 w-4 inline" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
